import '../../../shared/comments/comment.less';

import {
	CheckCircleTwoTone,
	DeleteOutlined,
	DragOutlined,
	EditOutlined,
	ExclamationCircleTwoTone,
	HomeOutlined,
	InfoCircleTwoTone,
	SmallDashOutlined,
	UserOutlined,
} from '@ant-design/icons';
import ReakitMenuButton from '@components/shared/buttons/ReakitMenuButton';
import {
	getCommentFieldName,
} from '@components/shared/comments/util/util';
import AccessiblePopconfirm from '@components/statements/table/AccessiblePopconfirm';
import { SubjectCommentStatus, SubjectCommentType } from '@services/src/models';
import { IAccountDetailsStore } from 'account/accountDetailsStore';
import { Button, Space, Tooltip } from 'antd';
import { ajaxByUser } from 'helper/api';
import { useRootData } from 'hooks/hook';
import React, { useState } from 'react';
import { Menu, MenuItem, useMenuState } from 'reakit/Menu';
import * as SubjectCommentTypeMapper from 'services/mappers/SubjectCommentType';
import { getProfiBazaApiClient } from 'services/ProfiBazaApi';

import { SubjectCommentDto } from '../commands/SubjectCommentsCommand';
import { SubjectCommentFields } from '../SubjectCommentStore';
import moment from 'moment';

interface IProps {
	refresh: () => void;
	data: SubjectCommentDto | undefined;
	subjectId: string;
	setEditable: (
		value: boolean,
		id: string | undefined,
		visible: boolean
	) => void;
}

const getCommentClass = (
	status: SubjectCommentStatus,
	type: SubjectCommentType
): string => {
	if (type === SubjectCommentType.IMPORTANT) {
		return 'antsay-comment important';
	}

	switch (status) {
		case SubjectCommentStatus.PROCESSED:
			return 'antsay-comment processed';
		default:
			return 'antsay-comment added';
	}
};

const getCommentIcon = (
	type: SubjectCommentType,
	title: string
): JSX.Element => {
	switch (type) {
		case SubjectCommentType.PUBLIC:
			return (
				<InfoCircleTwoTone
					twoToneColor="#40a9c7"
					aria-label={title}
					role="img"
				/>
			);
		case SubjectCommentType.PRIVATE:
			return <UserOutlined aria-label={title} role="img" />;
		case SubjectCommentType.IMPORTANT:
			return (
				<ExclamationCircleTwoTone
					twoToneColor="red"
					aria-label={title}
					role="img"
				/>
			);
	}
};

const getCommentContentClass = (status: SubjectCommentStatus): string => {
	switch (status) {
		case SubjectCommentStatus.PROCESSED:
			return 'content';
		default:
			return 'content important';
	}
};

const Comment = (props: IProps) => {
	const accountDetailsStore: IAccountDetailsStore = useRootData(
		(store) => store.accountDetailsStore
	);

	const menuState = useMenuState();

	const [deletePopconfirmVisible, setDeletePopconfirmVisible] = useState<
		boolean
	>(false);

	const hasMenuAccess =
		props.data?.author?.id ==
		accountDetailsStore.account.get()?.subject?.id;

	const menu = (
		<Menu
			{...menuState}
			aria-label="Menu komentarza"
			className="ant-menu ant-menu-sub account-menu-vertical z-index-up"
		>
			<MenuItem
				key="edit"
				className="ant-menu-item sub-menu-item"
				{...menuState}
				onClick={() => props.setEditable(true, props.data?.id!, true)}
			>
				<EditOutlined />
				Edytuj
			</MenuItem>

			<MenuItem
				key="delete"
				className="ant-menu-item sub-menu-item"
				{...menuState}
				onClick={() => {
					setDeletePopconfirmVisible(true);
				}}
			>
				<DeleteOutlined />
				Usuń
			</MenuItem>
		</Menu>
	);

	const { data } = props;

	const CommentIcon = () => {
		const title = SubjectCommentTypeMapper.map(props.data?.type!);

		return (
			<div>
				<Tooltip title={title}>
					{getCommentIcon(props.data?.type!, title)}
				</Tooltip>
			</div>
		);
	};

	const processComment = (comment: SubjectCommentDto) => {
		ajaxByUser('Rozpatrzono komentarz', () =>
			getProfiBazaApiClient().then((api) =>
				api.subjectComments
					.setStatus({
						body: {
							commentId: comment.id,
							commentStatus: SubjectCommentStatus.PROCESSED,
						},
					})
					.finally(props.refresh)
			)
		);
	};

	const CommentAction = (comment: SubjectCommentDto): JSX.Element => {
		if (comment.status === SubjectCommentStatus.PROCESSED) {
			return (
				<Space>
					<p>Rozpatrzono komentarz</p>
					<CheckCircleTwoTone
						twoToneColor="#52c41a"
						className="icon"
					/>
				</Space>
			);
		}

		return (
			<Button type="primary" onClick={() => processComment(data!)}>
				Rozpatrzono
			</Button>
		);
	};

	const ProperDate = (date: any) =>{
		const now = moment();
		if(now.isBefore(date))
			return now.fromNow();
		
		return date.fromNow();
	}

	const CommentBody = () => {
		return (
			<div>
				<div className={getCommentContentClass(props.data?.status!)}>
					{data?.body || ''}
				</div>
				<div className="footer-row">
					<UserOutlined />
					<p>{data?.author?.name}</p>
					<div>
						{data?.updatedAt ? (
							<Tooltip title={data?.updatedAtFormatted}>
								<span>{ProperDate(data?.updatedAt)}</span>
							</Tooltip>
						) : (
							<Tooltip title={data?.createdAtFormatted}>
								<span>{ProperDate(data?.createdAt)}</span>
							</Tooltip>
						)}
					</div>
				</div>
				<div className="footer-row">
					<HomeOutlined />
					<p>{`Podmiot (${data?.author.subject})`}</p>
				</div>
				<div className="field-desc">
					<DragOutlined />
					<p>
						{`Nazwa pola: ${getCommentFieldName(
							data?.fieldName!,
							SubjectCommentFields
						)}`}
					</p>
				</div>
				<div className="action">{CommentAction(data!)}</div>
			</div>
		);
	};

	const CommentMenu = (
		<>
			<ReakitMenuButton menuState={menuState} ariaLabel="Menu">
				<SmallDashOutlined />
			</ReakitMenuButton>
			{menu}
		</>
	);

	return (
		<AccessiblePopconfirm
			visible={deletePopconfirmVisible}
			title="Czy na pewno chcesz usunąć komentarz?"
			onConfirm={() => {
				ajaxByUser(
					'Usunięto komentarz',
					() =>
						getProfiBazaApiClient().then((api) =>
							api.subjectComments.deleteMethod({
								body: {
									commentId: props.data?.id!,
								},
							})
						),
					() => {
						setDeletePopconfirmVisible(false);
						props.refresh();
					}
				);
			}}
			onCancel={() => {
				setDeletePopconfirmVisible(false);
			}}
		>
			<div
				className={getCommentClass(
					props.data?.status!,
					props.data?.type!
				)}
			>
				<CommentIcon />
				<CommentBody />
				{hasMenuAccess && CommentMenu}
			</div>
		</AccessiblePopconfirm>
	);
};
export default Comment;
